import { cur } from '@/lib/currency';
import { useEffect, useState, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Search, RefreshCw, Trash2, Check, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtSR = (n) => `${cur()} ${Number(n || 0).toLocaleString('en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Find My Orphans (iter504) — Resolve supplier_payments whose
 * supplier_id is missing or points at a deleted supplier. Each row
 * shows the orphan details + a supplier autocomplete picker, with
 * Re-assign and Delete actions inline. No auto-fix exists for this
 * category because the right answer can't be guessed.
 */
export default function OrphanSupplierPaymentsPage() {
  const [orphans, setOrphans] = useState([]);
  const [count, setCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  // per-row picker state: { [paymentId]: supplierId }
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(null); // payment id currently submitting

  const fetchData = async () => {
    setLoading(true);
    try {
      const [r, sup] = await Promise.all([
        api.get('/app-audit/orphans/supplier-payments'),
        api.get('/suppliers').catch(() => ({ data: [] })),
      ]);
      setOrphans(r.data?.orphans || []);
      setCount(r.data?.count || 0);
      setTotalAmount(r.data?.total_amount || 0);
      const supList = Array.isArray(sup.data) ? sup.data : (sup.data?.suppliers || []);
      setSuppliers(supList);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load orphans');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const reassign = async (o) => {
    const sid = picks[o.id];
    if (!sid) { toast.error('Pick a supplier first'); return; }
    setBusy(o.id);
    try {
      await api.post(`/app-audit/orphans/supplier-payments/${o.id}/reassign`, { supplier_id: sid });
      toast.success('Re-assigned');
      setOrphans((prev) => prev.filter((x) => x.id !== o.id));
      setCount((c) => c - 1);
      setTotalAmount((t) => t - Number(o.amount || 0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to re-assign');
    } finally { setBusy(null); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete this orphan supplier_payment (${fmtSR(o.amount)} on ${(o.date || '').slice(0, 10)})? This cannot be undone.`)) return;
    setBusy(o.id);
    try {
      await api.delete(`/app-audit/orphans/supplier-payments/${o.id}`);
      toast.success('Deleted');
      setOrphans((prev) => prev.filter((x) => x.id !== o.id));
      setCount((c) => c - 1);
      setTotalAmount((t) => t - Number(o.amount || 0));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    } finally { setBusy(null); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="orphans-page">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/app-audit" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-audit">
            <ArrowLeft size={12} /> Back to App Audit
          </Link>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-lg shadow-rose-200/50">
              <AlertTriangle size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" data-testid="orphans-title">Find My Orphans · Supplier Payments</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Supplier payments whose supplier was deleted (or never set). Re-assign to an active supplier, or delete if the payment isn't real.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="orphans-refresh">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <Card data-testid="orphans-summary">
          <CardContent className="p-4 flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Orphans</div>
              <div className="text-3xl font-bold tabular-nums" data-testid="orphans-count">{count}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Amount</div>
              <div className="text-3xl font-bold tabular-nums text-rose-600" data-testid="orphans-total">{fmtSR(totalAmount)}</div>
            </div>
            {count === 0 && !loading && (
              <Badge variant="outline" className="border-emerald-400 text-emerald-700 ml-auto">
                <Check size={11} className="mr-1" /> All clean — no orphans
              </Badge>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Loading orphans…</CardContent></Card>
        ) : orphans.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Check size={32} className="mx-auto text-emerald-500 mb-2" />
              <p className="text-sm font-semibold">Nothing to fix here</p>
              <p className="text-xs text-muted-foreground mt-1">Every supplier_payment in this tenant points to an active supplier.</p>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="orphans-list">
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {orphans.map((o) => (
                  <OrphanRow
                    key={o.id}
                    orphan={o}
                    suppliers={suppliers}
                    pickedSupplier={picks[o.id] || ''}
                    onPickSupplier={(v) => setPicks((p) => ({ ...p, [o.id]: v }))}
                    onReassign={() => reassign(o)}
                    onDelete={() => remove(o)}
                    busy={busy === o.id}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

function OrphanRow({ orphan: o, suppliers, pickedSupplier, onPickSupplier, onReassign, onDelete, busy }) {
  const [query, setQuery] = useState('');
  const [serverResults, setServerResults] = useState(null); // null = use prop suppliers; array = server-side hits

  // iter505 — Server-side supplier search via /suppliers/names?q=…
  // Debounces 250ms so we don't fire per-keystroke. When the input is
  // empty we fall back to the prop `suppliers` (the initial /suppliers
  // payload). This is critical for tenants with thousands of
  // suppliers where the full list would otherwise overflow the Select.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setServerResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/suppliers/names', { params: { q, limit: 200 } });
        setServerResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        setServerResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const filtered = useMemo(() => {
    if (serverResults !== null) return serverResults;
    const q = query.trim().toLowerCase();
    if (!q) return suppliers.slice(0, 200);
    return suppliers.filter((s) => (s.name || '').toLowerCase().includes(q)
      || (s.id || '').toLowerCase().includes(q)).slice(0, 200);
  }, [suppliers, query, serverResults]);

  return (
    <div className="px-4 py-3 flex items-start gap-4 flex-wrap" data-testid={`orphan-row-${o.id}`}>
      <div className="flex-1 min-w-[260px]">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="text-sm font-semibold tabular-nums" data-testid={`orphan-amount-${o.id}`}>{fmtSR(o.amount)}</span>
          <Badge variant="outline" className="text-[10px]">{(o.date || '').slice(0, 10)}</Badge>
          <Badge variant="outline" className="text-[10px]">{o.branch_name || '—'}</Badge>
          <Badge variant="outline" className={`text-[10px] ${o.reason === 'supplier_deleted' ? 'border-rose-300 text-rose-700' : 'border-amber-300 text-amber-700'}`} data-testid={`orphan-reason-${o.id}`}>
            {o.reason === 'supplier_deleted' ? 'supplier deleted' : 'no supplier_id'}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground truncate" data-testid={`orphan-desc-${o.id}`}>
          {o.description || o.notes || '—'} {o.reference ? <span className="text-[10px] ml-1 opacity-60">· {o.reference}</span> : null}
        </div>
        {o.supplier_name ? <div className="text-[10px] text-muted-foreground mt-0.5">Last known name: {o.supplier_name}</div> : null}
      </div>
      <div className="flex flex-col gap-1.5 w-full sm:w-[280px]">
        <div className="relative">
          <Input
            placeholder="Search suppliers…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 text-xs pl-7"
            data-testid={`orphan-search-${o.id}`}
          />
          <Search size={11} className="absolute left-2 top-2.5 text-muted-foreground" />
        </div>
        <Select value={pickedSupplier} onValueChange={onPickSupplier}>
          <SelectTrigger className="h-8 text-xs" data-testid={`orphan-supplier-select-${o.id}`}>
            <SelectValue placeholder={`${filtered.length} suppliers — pick one`} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {filtered.map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`orphan-supplier-opt-${o.id}-${s.id}`}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <Button
          size="sm"
          className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700"
          onClick={onReassign}
          disabled={!pickedSupplier || busy}
          data-testid={`orphan-reassign-${o.id}`}
        >
          <Check size={12} className="mr-1" /> Re-assign
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-[11px] border-rose-300 text-rose-700"
          onClick={onDelete}
          disabled={busy}
          data-testid={`orphan-delete-${o.id}`}
        >
          <Trash2 size={12} className="mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}
